import * as THREE from 'three';

/**
 * BIM cross-line extraction (the former BIMOverlayLayer).
 *
 * The wireframe cross-reference overlays were replaced by BIMShadedLayer
 * (rendering/BIMShadedLayer.tsx), which mounts the BIM workbench's own
 * shaded element renderer in every shared viewport. What remains here is the
 * geometry-to-polylines extraction used by the 2D workbench's
 * BimDrawingUnderlay to project BIM drawing views as line-work.
 */

type Pt3 = [number, number, number];

/**
 * Build cross-reference polylines from a BIM element's geometryData.
 * Z-up world (A5): elevation/height rides the Z coordinate, ground XY is
 * the planar domain — exactly how the kernel stores the data.
 * (Exported: the 2D workbench's BimDrawingUnderlay reuses the same
 * extraction to project drawing-view underlays.)
 */
export function extractCrossLines(element: any): Pt3[][] {
    const gd = element.geometryData;
    if (!gd) return [];
    const cat: string = element.category;

    switch (cat) {
        case 'Walls': {
            // Plan footprint rectangle at the base offset: centerline
            // endpoints offset by half thickness, plus the top outline.
            if (!gd.start || !gd.end) return [];
            const start = gd.start as Pt3;
            const end = gd.end as Pt3;
            const dir = new THREE.Vector3(end[0] - start[0], end[1] - start[1], 0);
            const len = dir.length();
            if (len < 1e-6) return [];
            dir.divideScalar(len);
            const perp = new THREE.Vector3(-dir.y, dir.x, 0).multiplyScalar((gd.thickness ?? 0.2) / 2);
            const z0 = gd.baseOffset ?? 0;
            const z1 = z0 + (gd.height ?? 3);
            const corners = (z: number): Pt3[] => [
                [start[0] + perp.x, start[1] + perp.y, z],
                [end[0] + perp.x, end[1] + perp.y, z],
                [end[0] - perp.x, end[1] - perp.y, z],
                [start[0] - perp.x, start[1] - perp.y, z],
                [start[0] + perp.x, start[1] + perp.y, z],
            ];
            const base = corners(z0);
            const top = corners(z1);
            return [
                base,
                top,
                [base[0], top[0]],
                [base[1], top[1]],
                [base[2], top[2]],
                [base[3], top[3]],
            ];
        }

        case 'Structural Framing':
        case 'Structural Trusses':
        case 'Braces':
        case 'Precast Beams': {
            if (!gd.start || !gd.end) return [];
            return [[gd.start as Pt3, gd.end as Pt3]];
        }

        case 'Columns':
        case 'Structural Columns':
        case 'Precast Columns': {
            if (!Array.isArray(gd.location)) return [];
            const loc = gd.location as Pt3;
            const z0 = loc[2] + (gd.bottomOffset ?? 0);
            const z1 = z0 + (gd.height ?? 3);
            return [[
                [loc[0], loc[1], z0],
                [loc[0], loc[1], z1],
            ]];
        }

        case 'Ducts':
        case 'Flex Ducts':
        case 'Pipes':
        case 'Flex Pipes':
        case 'Cable Trays':
        case 'Conduits':
        case 'Wires':
        case 'Pipe Placeholders': {
            if (!Array.isArray(gd.path) || gd.path.length < 2) return [];
            return [gd.path as Pt3[]];
        }

        case 'Floors':
        case 'Ceilings':
        case 'Precast Floors': {
            const boundary = gd.boundary ?? gd.footprint;
            if (!Array.isArray(boundary) || boundary.length < 3) return [];
            const z = gd.elevation ?? gd.baseOffset ?? 0;
            const pts: Pt3[] = boundary.map((p: [number, number]) => [p[0], p[1], z] as Pt3);
            pts.push(pts[0]);
            return [pts];
        }

        case 'Roofs': {
            const boundary = gd.boundary ?? gd.footprint;
            if (!Array.isArray(boundary) || boundary.length < 3) return [];
            const z = gd.elevation ?? gd.baseOffset ?? 0;
            const pts: Pt3[] = boundary.map((p: [number, number]) => [p[0], p[1], z] as Pt3);
            pts.push(pts[0]);
            return [pts];
        }

        case 'Ramps': {
            if (!Array.isArray(gd.path) || gd.path.length < 2) return [];
            return [gd.path as Pt3[]];
        }

        default:
            return [];
    }
}
